
import React, { useEffect, useState } from 'react';
import AnimatedText from './AnimatedText';
import { ArrowRight } from 'lucide-react';

const Hero: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    >
      {/* Background shapes */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-presintention-accent/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-presintention-accent/10 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '1s' }} />
      </div>

      <div className="container mx-auto px-6 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <div>
            {/* Badge */}
            <div className={`inline-block mb-6 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
              <span className="bg-presintention-accent/10 text-presintention-accent px-3 py-1 rounded-full text-sm font-medium">
                Elevate Your Presentation Skills
              </span>
            </div>
            
            {/* Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-balance text-presintention-dark">
              <span className="text-reveal-container block mb-2">
                <span className={`block transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
                  Craft Presentations
                </span>
              </span>
              <span className="text-reveal-container block">
                <span className={`block transition-all duration-700 delay-200 text-presintention-accent ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
                  That Inspire Action
                </span>
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className={`text-lg md:text-xl text-presintention-dark/70 max-w-3xl mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
              Transform your public speaking with expert coaching that builds confidence, clarity, and charisma. Discover the techniques that captivate audiences and drive results.
            </p>
            
            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-presintention-accent text-white rounded-md hover:bg-opacity-90 transition-all button-transition hover-lift text-base md:text-lg font-medium"
              >
                Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href="#process"
                className="inline-flex items-center justify-center px-6 py-3 border border-presintention-border bg-white text-presintention-dark rounded-md hover:bg-presintention-light transition-all button-transition hover-lift text-base md:text-lg font-medium"
              >
                Learn Our Process
              </a>
            </div>
          </div>
          
          {/* Hero Image */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-presintention-accent/20 to-transparent rounded-2xl transform -rotate-6"></div>
              <img 
                src="/lovable-uploads/8a1d8ca9-a01f-4aae-9ab9-2d6d648a37c1.png" 
                alt="Professional presentation coach" 
                className="relative z-10 rounded-2xl shadow-xl w-full"
              />
              <div className="mt-2 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <h3 className="font-bold text-lg text-presintention-dark">Alex Fairlie</h3>
                  <span className="text-presintention-dark/70">•</span>
                  <p className="text-presintention-dark/70">Presentation Coach</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-16 md:h-24 overflow-hidden">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="absolute bottom-0 w-full h-auto">
          <path fill="#f8f9fa" fillOpacity="0.4" d="M0,288L60,266.7C120,245,240,203,360,176C480,149,600,139,720,154.7C840,171,960,213,1080,224C1200,235,1320,213,1380,202.7L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="absolute bottom-0 w-full h-auto">
          <path fill="#ffffff" fillOpacity="0.8" d="M0,192L60,208C120,224,240,256,360,245.3C480,235,600,181,720,181.3C840,181,960,235,1080,240C1200,245,1320,203,1380,181.3L1440,160L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
